import React, { useEffect, useRef, useCallback, useState } from "react"
import { useLocation } from "react-router-dom"
import PropTypes from "prop-types"

// //Import Scrollbar
import SimpleBar from "simplebar-react"

// MetisMenu
import MetisMenu from "metismenujs"
import withRouter from "../Common/withRouter"
import { Link } from "react-router-dom"

//i18n
import { withTranslation } from "react-i18next"
import { getUserPermissions, hasPagePermissionWithAdmin, isUserAdmin } from "../Permission/PermissionService"

const SidebarContent = props => {
  const ref = useRef()
  const [permissions, setPermissions] = useState([]);
  const [metisMenuInstance, setMetisMenuInstance] = useState(null);
  const metisMenuInstanceRef = useRef(null);
  const isInitializingRef = useRef(false);
  const deferredActiveMenuTimeoutRef = useRef(null);
  const lastActiveMenuApplyRef = useRef({ path: null, ts: 0 });
  const path = useLocation();

  // Function to initialize MetisMenu
  const initializeMetisMenu = useCallback((reason = 'unknown', onComplete = null) => {
    // Prevent multiple simultaneous initializations
    if (isInitializingRef.current) {
      console.log(`[SIDEBAR] initializeMetisMenu already in progress, skipping - Reason: ${reason}`);
      return;
    }
    
    const sideMenu = document.getElementById("side-menu");
    if (!sideMenu) {
      console.warn('[SIDEBAR] Side menu element not found!');
      return;
    }
    
    // Check if sidebar is visible/expanded
    const isSidebarVisible = document.body.classList.contains('sidebar-enable') || 
                            !document.body.classList.contains('vertical-collpsed');
    
    isInitializingRef.current = true;
    
    // Clean up existing instance if any
    if (metisMenuInstanceRef.current) {
      try {
        metisMenuInstanceRef.current.dispose();
      } catch (e) {
        console.warn('[SIDEBAR] Error disposing MetisMenu:', e);
      }
    }
    
    // Delay to ensure DOM is ready, especially when transitioning from condensed to expanded
    const delay = reason.includes('manually-opened') || reason.includes('hamburger') ? 200 : 150;
    setTimeout(() => {
      try {
        // Double-check menu element still exists
        const sideMenuCheck = document.getElementById("side-menu");
        if (!sideMenuCheck) {
          console.warn('[SIDEBAR] Side menu element disappeared during initialization!');
          isInitializingRef.current = false;
          return;
        }
        
        const instance = new MetisMenu("#side-menu");
        metisMenuInstanceRef.current = instance;
        setMetisMenuInstance(instance);
        isInitializingRef.current = false;
        
        // Call onComplete callback after initialization if provided
        // Add a small delay to ensure MetisMenu is fully ready
        if (onComplete && typeof onComplete === 'function') {
          setTimeout(() => {
            onComplete();
          }, 50);
        }
      } catch (e) {
        console.error("[SIDEBAR] Error initializing MetisMenu:", e);
        isInitializingRef.current = false;
      }
    }, delay);
  }, []);

  // Hard reset helper to clear classes and force a clean MetisMenu instance
  const hardResetMetisMenu = useCallback((reason = 'unknown-reset', onComplete = null) => {
    const sideMenuElement = document.getElementById("side-menu");
    if (!sideMenuElement) return;
    try {
      if (metisMenuInstanceRef.current && typeof metisMenuInstanceRef.current.dispose === 'function') {
        metisMenuInstanceRef.current.dispose();
      }
    } catch (e) {
      console.warn('[SIDEBAR] Error disposing MetisMenu during hard reset:', e);
    }
    // Remove mm-* state classes to avoid stale state
    try {
      sideMenuElement.querySelectorAll('.mm-active').forEach(el => el.classList.remove('mm-active'));
      sideMenuElement.querySelectorAll('.mm-show').forEach(el => el.classList.remove('mm-show'));
      sideMenuElement.querySelectorAll('.has-arrow.mm-active').forEach(el => el.classList.remove('mm-active'));
    } catch (e) {
      // no-op
    }
    // Recreate with onComplete callback
    initializeMetisMenu(`hard-reset-${reason}`, onComplete);
  }, [initializeMetisMenu]);

  useEffect(() => {
    const loadPermissions = async () => {
      try {
        const userPermissions = await getUserPermissions();
        setPermissions(userPermissions);
        
        // Store permissions in localStorage so spa pages can access them
        localStorage.setItem('permissions', JSON.stringify(userPermissions));
        
        // Initialize MetisMenu after permissions are loaded
        setTimeout(() => {
          // Restore active menu state after initial MetisMenu initialization
          initializeMetisMenu('initial-permissions-loaded', () => {
            activeMenu();
          });
        }, 100);
      } catch (error) {
        console.error("[SIDEBAR] Error loading permissions:", error);
      }
    };
    loadPermissions();
  }, [initializeMetisMenu]);

  // Function to check if a menu item should be shown
  const shouldShowMenuItem = (route) => {
    const hasPermission = hasPagePermissionWithAdmin(permissions, route);
    return hasPermission;
  };

  // Function to get the appropriate dashboard route based on permissions
  const getDashboardRoute = () => {
    const hasAdminDashboardPermission = hasPagePermissionWithAdmin(permissions, '/GmsAdminDashboard');
    const hasDashboardPermission = hasPagePermissionWithAdmin(permissions, '/Dashboard');
    const hasSpaDashboardPermission = hasPagePermissionWithAdmin(permissions, '/GmsSpaEmployeeDashboard');
    const hasSpaManagerFinancePermission = hasPagePermissionWithAdmin(permissions, '/GmsSpaManagerFinanceDashboard');
    
    // Dashboard route now points to Admin Dashboard
    // If user has Dashboard permission (which now shows Admin Dashboard), return Dashboard
    if (hasDashboardPermission || hasAdminDashboardPermission) {
      return '/Dashboard';
    }
    // If user has Spa Manager & Finance Dashboard permission, show that
    if (hasSpaManagerFinancePermission) {
      return '/GmsSpaManagerFinanceDashboard';
    }
    // If user only has Spa Employee Dashboard permission, show that
    if (hasSpaDashboardPermission) {
      return '/GmsSpaEmployeeDashboard';
    }
    // Fallback to default dashboard for users without specific permissions
    return '/DefaultDashboard';
  };

  const activateParentDropdown = useCallback(item => {
    item.classList.add("active")
    const parent = item.parentElement
    const parent2El = parent.childNodes[1]

    if (parent2El && parent2El.id !== "side-menu") {
      parent2El.classList.add("mm-show")
    }

    if (parent) {
      parent.classList.add("mm-active")
      const parent2 = parent.parentElement

      if (parent2) {
        parent2.classList.add("mm-show") // ul tag

        const parent3 = parent2.parentElement // li tag

        if (parent3) {
          parent3.classList.add("mm-active") // li
          parent3.childNodes[0].classList.add("mm-active") //a
          const parent4 = parent3.parentElement // ul
          if (parent4) {
            parent4.classList.add("mm-show") // ul
            const parent5 = parent4.parentElement
            if (parent5) {
              parent5.classList.add("mm-show") // li
              parent5.childNodes[0].classList.add("mm-active") // a tag
            }
          }
        }
      }
      scrollElement(item)
      return false
    }
    scrollElement(item)
    return false
  }, [])

  const removeActivation = items => {
    for (var i = 0; i < items.length; ++i) {
      var item = items[i]
      const parent = items[i].parentElement

      if (item && item.classList.contains("active")) {
        item.classList.remove("active")
      }
      if (parent) {
        const parent2El =
          parent.childNodes && parent.childNodes.lenght && parent.childNodes[1]
            ? parent.childNodes[1]
            : null
        if (parent2El && parent2El.id !== "side-menu") {
          parent2El.classList.remove("mm-show")
        }

        parent.classList.remove("mm-active")
        const parent2 = parent.parentElement

        if (parent2) {
          parent2.classList.remove("mm-show")

          const parent3 = parent2.parentElement
          if (parent3) {
            parent3.classList.remove("mm-active") // li
            parent3.childNodes[0].classList.remove("mm-active")

            const parent4 = parent3.parentElement // ul
            if (parent4) {
              parent4.classList.remove("mm-show") // ul
              const parent5 = parent4.parentElement
              if (parent5) {
                parent5.classList.remove("mm-show") // li
                parent5.childNodes[0].classList.remove("mm-active") // a tag
              }
            }
          }
        }
      }
    }
  }

  const activeMenu = useCallback(() => {
    // During sidebar expand/collapse animation, DO NOT apply mm-show/mm-active.
    // Otherwise the submenu renders open during width animation and looks glitchy.
    if (document.body.classList.contains("sidebar-transitioning")) {
      if (deferredActiveMenuTimeoutRef.current) {
        clearTimeout(deferredActiveMenuTimeoutRef.current);
      }
      deferredActiveMenuTimeoutRef.current = setTimeout(() => {
        deferredActiveMenuTimeoutRef.current = null;
        // Retry once the sidebar is stable
        activeMenu();
      }, 500);
      return;
    }

    // Only apply active menu state when sidebar is expanded (not condensed)
    // When sidebar is condensed, we don't need to keep menus expanded/selected
    const isSidebarCondensed = props.sidebarType === "condensed" || 
                                document.body.classList.contains("vertical-collpsed");
    
    if (isSidebarCondensed) {
      console.log('[SIDEBAR] Sidebar is condensed - skipping active menu state restoration');
      return;
    }
    
    const pathName = path.pathname

    // Debounce: when expanding, multiple observers/effects can call activeMenu().
    // Avoid removeActivation -> activateParentDropdown twice (looks like a "refresh" flash).
    // However, allow calls after route changes even if recent (to restore menu state after MetisMenu re-init)
    const now = Date.now();
    const timeSinceLastApply = now - lastActiveMenuApplyRef.current.ts;
    
    // Only debounce if it's the same path and very recent (< 200ms), but allow if it's been longer (route change restore)
    if (lastActiveMenuApplyRef.current.path === pathName && timeSinceLastApply < 200) {
      return;
    }
    const ul = document.getElementById("side-menu")
    if (!ul) {
      console.warn('[SIDEBAR] Side menu element not found in activeMenu!');
      return;
    }
    
    let matchingMenuItem = null
    const items = ul.getElementsByTagName("a")
    removeActivation(items)

    for (let i = 0; i < items.length; ++i) {
      const itemPath = items[i].pathname || items[i].getAttribute('href');
      // Also check if the pathname matches (handles both absolute and relative paths)
      if (pathName === itemPath || pathName === items[i].pathname) {
        matchingMenuItem = items[i]
        break
      }
    }
    if (matchingMenuItem) {
      activateParentDropdown(matchingMenuItem)
      lastActiveMenuApplyRef.current = { path: pathName, ts: now };
    } else {
      console.log(`[SIDEBAR] No matching menu item found for path: ${pathName}`);
    }
  }, [path.pathname, activateParentDropdown, props.sidebarType])

  useEffect(() => {
    if (ref.current) {
      ref.current.recalculate()
    }
  }, [])

  // Cleanup any deferred activeMenu calls
  useEffect(() => {
    return () => {
      if (deferredActiveMenuTimeoutRef.current) {
        clearTimeout(deferredActiveMenuTimeoutRef.current);
        deferredActiveMenuTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    activeMenu()
  }, [activeMenu])

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" })
    
    // First, immediately activate the menu to show the active state
    // This ensures the menu is highlighted even before MetisMenu re-initializes
    activeMenu();
    
    // Re-initialize MetisMenu after route change to fix hover issues
    // This ensures MetisMenu's event handlers are properly attached after navigation
    // After re-initialization, restore the active menu state
    const routeChangeTimeout = setTimeout(() => {
      if (!isInitializingRef.current) {
        // Pass activeMenu as callback to restore active state after re-initialization
        hardResetMetisMenu(`route-change-${path.pathname}`, () => {
          // Add a delay to ensure MetisMenu is fully ready and DOM is stable before activating
          setTimeout(() => {
            activeMenu();
            // Call it again after a short delay to ensure it persists (MetisMenu might reset it)
            setTimeout(() => {
              activeMenu();
            }, 150);
          }, 150);
        });
      } else {
        console.log('[SIDEBAR] Skipping route-change initialization - already initializing');
        // If already initializing, just call activeMenu after a delay
        setTimeout(() => {
          activeMenu();
        }, 400);
      }
    }, 200);
    
    return () => {
      clearTimeout(routeChangeTimeout);
    };
  }, [activeMenu, hardResetMetisMenu, path.pathname])

  // Re-initialize MetisMenu when sidebar type changes (e.g., condensed <-> default)
  // This ensures menu works properly when sidebar is manually toggled
  useEffect(() => {
    if (props.sidebarType) {
      // Wait for CSS transitions to complete after sidebar type change
      const timeoutId = setTimeout(() => {
        const sideMenu = document.getElementById("side-menu");
        if (sideMenu && !isInitializingRef.current) {
          console.log('[SIDEBAR] Hard resetting MetisMenu due to sidebar type change');
          // Restore active menu state after re-initialization
          hardResetMetisMenu(`sidebar-type-changed-${props.sidebarType}`, () => {
            console.log('[SIDEBAR] MetisMenu re-initialized after sidebar type change, restoring active state');
            activeMenu();
          });
        }
      }, 400); // Longer delay to ensure CSS transitions complete
      
      return () => clearTimeout(timeoutId);
    }
  }, [props.sidebarType, hardResetMetisMenu, activeMenu])

  // Re-initialize MetisMenu when sidebar is manually opened (especially after auto-hide)
  useEffect(() => {
    let timeoutId = null;
    let lastSidebarEnabled = false;
    
    // Get initial state
    if (document.body) {
      lastSidebarEnabled = document.body.classList.contains('sidebar-enable');
    }
    
    // Listen specifically for sidebar-enable class changes (when user clicks hamburger)
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const body = document.body;
          const currentSidebarEnabled = body.classList.contains('sidebar-enable');
          
          // Check if sidebar-enable state actually changed
          if (currentSidebarEnabled !== lastSidebarEnabled) {
            console.log(`[SIDEBAR] sidebar-enable changed: ${lastSidebarEnabled} -> ${currentSidebarEnabled}`);
            lastSidebarEnabled = currentSidebarEnabled;
            
            // When sidebar is manually opened (sidebar-enable becomes true), re-initialize MetisMenu
            if (currentSidebarEnabled) {
              console.log('[SIDEBAR] Sidebar manually opened - re-initializing MetisMenu');
              console.log('[SIDEBAR] Current body classes:', body.className);
              
              // Clear any pending timeouts
              if (timeoutId) {
                clearTimeout(timeoutId);
              }
              
              // Re-initialize when sidebar is opened - use longer delay to ensure DOM is fully updated
              // This is especially important when transitioning from condensed to expanded mode
              timeoutId = setTimeout(() => {
                // Double-check that sidebar is still enabled and menu element exists and is visible
                const sideMenu = document.getElementById("side-menu");
                const sidebarElement = document.querySelector('.vertical-menu');
                const isSidebarVisible = sidebarElement && 
                                        (window.getComputedStyle(sidebarElement).display !== 'none' &&
                                         window.getComputedStyle(sidebarElement).visibility !== 'hidden');
                
                if (sideMenu && document.body.classList.contains('sidebar-enable') && !isInitializingRef.current) {
                  // If sidebar element exists, check if it's visible; otherwise proceed anyway
                  if (sidebarElement && !isSidebarVisible) {
                    console.log('[SIDEBAR] Sidebar element exists but not visible yet, waiting a bit more...');
                    // Wait a bit more for CSS transitions to complete
                    setTimeout(() => {
                      if (!isInitializingRef.current) {
                        console.log('[SIDEBAR] Executing MetisMenu hard reset after visibility check');
                        hardResetMetisMenu('sidebar-manually-opened-delayed', () => {
                          console.log('[SIDEBAR] MetisMenu re-initialized after manual open (delayed), restoring active state');
                          activeMenu();
                        });
                      }
                    }, 200);
                  } else {
                    console.log('[SIDEBAR] Executing MetisMenu hard reset for manual sidebar open');
                    console.log('[SIDEBAR] Side menu element found, re-initializing...');
                    hardResetMetisMenu('sidebar-manually-opened', () => {
                      console.log('[SIDEBAR] MetisMenu re-initialized after manual open, restoring active state');
                      activeMenu();
                    });
                  }
                } else {
                  console.warn('[SIDEBAR] Cannot re-initialize: sideMenu exists:', !!sideMenu, 
                              'sidebar-enabled:', document.body.classList.contains('sidebar-enable'), 
                              'isInitializing:', isInitializingRef.current,
                              'sidebar visible:', isSidebarVisible);
                }
              }, 300); // Increased delay to ensure DOM transition is complete
            }
          }
        }
      });
    });

    // Observe body class changes
    if (document.body) {
      observer.observe(document.body, {
        attributes: true,
        attributeFilter: ['class']
      });
    }

    // Also listen for click events on the hamburger button as a backup
    // Use event delegation on document to catch clicks even if button isn't ready yet
    const handleHamburgerClick = (e) => {
      // Check if the click was on the hamburger button or its children
      const target = e.target;
      const hamburgerButton = document.getElementById('vertical-menu-btn');
      if (hamburgerButton && (target === hamburgerButton || hamburgerButton.contains(target))) {
        console.log('[SIDEBAR] Hamburger button clicked');
        console.log('[SIDEBAR] Current sidebar-enable state before click:', document.body.classList.contains('sidebar-enable'));
        
        // Wait for the class toggle to complete, then re-initialize
        // Use longer delay to ensure DOM is fully updated, especially when transitioning from condensed mode
        setTimeout(() => {
          const isSidebarEnabled = document.body.classList.contains('sidebar-enable');
          const sideMenu = document.getElementById("side-menu");
          const sidebarElement = document.querySelector('.vertical-menu');
          const isSidebarVisible = sidebarElement && 
                                  (window.getComputedStyle(sidebarElement).display !== 'none' &&
                                   window.getComputedStyle(sidebarElement).visibility !== 'hidden');
          
          console.log('[SIDEBAR] After click - sidebar-enabled:', isSidebarEnabled, 
                     'sideMenu exists:', !!sideMenu, 'sidebar visible:', isSidebarVisible);
          
          if (isSidebarEnabled && sideMenu && !isInitializingRef.current) {
            // Check if sidebar is visible, if not wait a bit more
            if (sidebarElement && !isSidebarVisible) {
              console.log('[SIDEBAR] Sidebar not fully visible yet, waiting for CSS transition...');
              setTimeout(() => {
                if (document.body.classList.contains('sidebar-enable') && !isInitializingRef.current) {
                  console.log('[SIDEBAR] Sidebar opened via hamburger (delayed) - hard resetting MetisMenu');
                  hardResetMetisMenu('hamburger-button-clicked-delayed', () => {
                    console.log('[SIDEBAR] MetisMenu re-initialized after hamburger click (delayed), restoring active state');
                    activeMenu();
                  });
                }
              }, 200);
            } else {
              console.log('[SIDEBAR] Sidebar opened via hamburger - hard resetting MetisMenu');
              hardResetMetisMenu('hamburger-button-clicked', () => {
                console.log('[SIDEBAR] MetisMenu re-initialized after hamburger click, restoring active state');
                activeMenu();
              });
            }
          } else if (!isSidebarEnabled) {
            console.log('[SIDEBAR] Sidebar was closed, skipping re-initialization');
          } else {
            console.warn('[SIDEBAR] Cannot re-initialize after hamburger click:', {
              isSidebarEnabled,
              sideMenuExists: !!sideMenu,
              isInitializing: isInitializingRef.current
            });
          }
        }, 350); // Increased delay to ensure DOM transition is complete
      }
    };
    
    // Use event delegation on document body to catch clicks
    document.body.addEventListener('click', handleHamburgerClick);

    return () => {
      console.log('[SIDEBAR] Cleaning up sidebar listeners');
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      observer.disconnect();
      document.body.removeEventListener('click', handleHamburgerClick);
    };
  }, [initializeMetisMenu, hardResetMetisMenu, activeMenu]);

  // Cleanup MetisMenu instance on unmount
  useEffect(() => {
    return () => {
      console.log('[SIDEBAR] Component unmounting - disposing MetisMenu');
      if (metisMenuInstanceRef.current) {
        try {
          metisMenuInstanceRef.current.dispose();
          console.log('[SIDEBAR] MetisMenu disposed successfully');
        } catch (e) {
          console.warn('[SIDEBAR] Error disposing MetisMenu on unmount:', e);
        }
      }
    };
  }, []);

  function scrollElement(item) {
    if (item) {
      const currentPosition = item.offsetTop
      if (currentPosition > window.innerHeight) {
        ref.current.getScrollElement().scrollTop = currentPosition - 300
      }
    }
  }

  // Define menu items with their routes
  // 1. Customers & Bookings
  const customerMenuItems = [
    { route: "/GmsCustomer", label: "New Customer" },
    { route: "/GmsCustomerList", label: "Customer List" },
  ];

  const bookingMenuItems = [
    { route: "/GmsSpaCalendar", label: "Booking Calendar", icon: "bx bx-calendar" },
    { route: "/GmsAddBooking", label: "New Booking", icon: "bx bx-plus-circle" },
    { route: "/GmsBooking", label: "Booking List", icon: "bx bx-calendar-check" },
  ];

  const membershipsMenuItems = [
    { route: "/GmsMembership", label: "Membership Plans", icon: "bx bx-id-card" },
  ];

  // 2. Sales & Payments
  const salesPosMenuItems = [
    { route: "/GmsBilling", label: "Add Invoice", icon: "bx bx-receipt" },
    { route: "/GmsBillingList", label: "Invoice List", icon: "bx bx-list-ul" },
  ];

  const receiptsPaymentsMenuItems = [
    { route: "/GmsAddReceipt", label: "Add Receipt", icon: "bx bx-money" },
    { route: "/GmsCustomerReceipts", label: "Receipts List", icon: "bx bx-list-ul" },
    { route: "/GmsAddCashDeposit", label: "Add Cash Deposit", icon: "bx bx-wallet" },
    { route: "/GmsCashDeposit", label: "Cash Deposits List", icon: "bx bx-list-ul" },
  ];

  const pettyCashMenuItems = [
    { route: "/GmsPettyCashReceipts", label: "Petty Cash In", icon: "bx bx-receipt" },
    { route: "/GmsPettyCashPayments", label: "Petty Cash Out", icon: "bx bx-money-withdraw" },
    { route: "/GmsPettyCashReport", label: "Petty Cash Report", icon: "bx bx-list-ul" },
  ];

  const dayClosingMenuItems = [
    { route: "/GmsDailyClosing", label: "X Report", icon: "bx bx-calendar-check" },
    { route: "/GmsZReport", label: "Z Report", icon: "bx bx-file-blank" },
  ];

  // 3. Services & Packages
  const servicesMenuItems = [
    { route: "/GmsSpaService", label: "Service Catalogue", icon: "bx bx-spa" },
    { route: "/GmsSpaCategory", label: "Categories", icon: "bx bx-category" },
    { route: "/GmsSpaSubCategory", label: "Subcategories", icon: "bx bx-subdirectory-right" },
  ];

  const packagesMenuItems = [
    { route: "/GmsSpaPackages", label: "Packages", icon: "bx bx-package" },
    { route: "/GmsSpaPackageSeries", label: "Series Packages", icon: "bx bx-collection" },
  ];

  // 4. Inventory & Suppliers
  const productsAssetsMenuItems = [
    { route: "/GmsProductManagement", label: "Products" },
    { route: "/GmsAddProduct", label: "Add Product" },
    { route: "/GmsRoom", label: "Rooms" },
    { route: "/GmsEquipment", label: "Equipment", icon: "bx bx-wrench" },
  ];

  const inventoryMenuItems = [
    { route: "/GmsStockTransfer", label: "Stock Transfer", icon: "bx bx-transfer" },
    { route: "/GmsReceiveStockTransfer", label: "Receive Stock", icon: "bx bx-check-circle" },
  ];

  const suppliersMenuItems = [
    { route: "/GmsSupplier", label: "Supplier Management" },
    { route: "/GmsSupplierCategory", label: "Supplier Category" },
  ];

  const purchaseMenuItems = [
    { route: "/GmsPurchaseOrder", label: "Purchase Order" },
    { route: "/GmsAddPurchaseOrder", label: "Add Purchase Order" },
    { route: "/GmsPurchase", label: "Purchases" },
    { route: "/GmsAddPurchase", label: "Add Purchase" },
  ];

  // 5. Human Resources
  const employeesMenuItems = [
    { route: "/GmsEmployee", label: "Add / Edit Employee" },
    { route: "/GmsExpiringDocuments", label: "Expiring Documents" },
  ];

  const attendanceLeaveMenuItems = [
    { route: "/GmsDailyAttendance", label: "Daily Attendance" },
    { route: "/GmsEmployeeLeave", label: "Leave Request" },
    { route: "/GmsLeaveType", label: "Leave Types" },
    // { route: "/GmsLeaveCalculator", label: "Leave Calculator" }, // Hidden as per requirements
    { route: "/GmsEmployeeLeaveCredit", label: "Update Leave Balances" },
    { route: "/GmsAnnualLeaveCredit", label: "Annual Leave Credit Processing", icon: "bx bx-calendar-check" },
  ];

  const salaryAdvanceRevisionMenuItems = [
    { route: "/GmsSalaryAdvance", label: "Salary Advance" },
    { route: "/GmsSalaryRevision", label: "Salary Revision", icon: "bx bx-money" },
  ];

  const payrollMenuItems = [
    { route: "/GmsPayrollProcessing", label: "Payroll Processing" },
  ];

  // 6. Reports
  const salesOperationalReportsMenuItems = [
    { route: "/GmsInvoiceReport", label: "Sales Reports" },
    { route: "/GmsBookingReport", label: "Booking Reports" },
    { route: "/GmsStockReport", label: "Stock Report", icon: "bx bx-bar-chart" },
    { route: "/GmsDailySummary", label: "Daily Summary Report" },
    { route: "/GmsSourceBasedReport", label: "Source Based Customer Count and Revenue" },
  ];

  const employeePerformanceReportsMenuItems = [
    { route: "/GmsDaybook", label: "Day Book" },
    { route: "/GmsSalesbook", label: "Sales Book" },
  ];

  const hrReportsMenuItems = [
    { route: "/GmsSalaryAdvanceTransactionReport", label: "Salary Advance Transaction Report" },
    { route: "/GmsPayrollHistoryReport", label: "Payroll History Report" },
    { route: "/GmsPayrollDetailsHistoryReport", label: "Payroll Details History Report" },
    { route: "/GmsEmployeeReport", label: "Employee Report" },
  ];

  // 7. Settings (Admin Only)
  const businessSetupMenuItems = [
    { route: "/GmsCompany", label: "Group" },
    { route: "/GmsBusiness", label: "Company" },
    { route: "/GmsBranch", label: "Branch" },
    { route: "/GmsDepartment", label: "Department" },
    { route: "/GmsDesignation", label: "Designation" },
    { route: "/GmsTax", label: "Tax", icon: "bx bx-calculator" },
    { route: "/GmsSource", label: "Source Management" },
  ];

  const financeSetupMenuItems = [
    { route: "/GmsBank", label: "Banks", icon: "bx bx-building" },
    { route: "/GmsBankAccount", label: "Bank Accounts", icon: "bx bx-credit-card" },
    { route: "/GmsCostHead", label: "Cost Heads", icon: "bx bx-money" },
    { route: "/GmsOtherPayee", label: "Other Payee", icon: "bx bx-user" },
  ];

  const userManagementMenuItems = [
    { route: "/GmsUser", label: "User Management" },
    { route: "/GmsRoleManagement", label: "Role Management" },
  ];

  const notificationsMenuItems = [
    { route: "/GmsNotifications", label: "Notifications" },
  ];

  // 8. Others
  const othersMenuItems = [
    { route: "/GmsService", label: "Service Management" },
    { route: "/GmsActivityManagement", label: "Activity Management" },
    { route: "/GmsActivityUpdated", label: "Activities", icon: "bx bx-refresh" },
    { route: "/GmsStockLockManagement", label: "Stock Lock Management", icon: "bx bx-lock" },
  ];


  return (
    <React.Fragment>
      <SimpleBar className="h-100" ref={ref}>
        <div id="sidebar-menu">
          <ul className="metismenu list-unstyled" id="side-menu">
            <li className="menu-title">{props.t("Menu")}</li>

            {/* Dashboard */}
            <li>
              <Link to={getDashboardRoute()}>
                <i className="bx bx-home-circle"></i>
                <span>{props.t("Dashboard")}</span>
              </Link>
            </li>

            {/* 1. Customers & Bookings Menu */}
            {(customerMenuItems.some(item => shouldShowMenuItem(item.route)) || 
              bookingMenuItems.some(item => shouldShowMenuItem(item.route)) || 
              membershipsMenuItems.some(item => shouldShowMenuItem(item.route))) && (
              <li>
                <Link to="/#" className="has-arrow">
                  <i className="bx bx-user"></i>
                  <span>{props.t("Customers & Bookings")}</span>
                </Link>
                <ul className="sub-menu">
                  {/* Customers Submenu */}
                  {customerMenuItems.some(item => shouldShowMenuItem(item.route)) && (
                    <li>
                      <Link to="/#" className="has-arrow">
                        <span>{props.t("Customers")}</span>
                      </Link>
                      <ul className="sub-menu">
                        {customerMenuItems.map((item) => 
                          shouldShowMenuItem(item.route) && (
                            <li key={item.route}>
                              <Link to={item.route}>{props.t(item.label)}</Link>
                            </li>
                          )
                        )}
                      </ul>
                    </li>
                  )}
                  
                  {/* Bookings Submenu */}
                  {bookingMenuItems.some(item => shouldShowMenuItem(item.route)) && (
                    <li>
                      <Link to="/#" className="has-arrow">
                        <span>{props.t("Bookings")}</span>
                      </Link>
                      <ul className="sub-menu">
                        {bookingMenuItems.map((item) => 
                          shouldShowMenuItem(item.route) && (
                            <li key={item.route}>
                              <Link to={item.route}>{props.t(item.label)}</Link>
                            </li>
                          )
                        )}
                      </ul>
                    </li>
                  )}
                  
                  {/* Memberships Submenu */}
                  {membershipsMenuItems.some(item => shouldShowMenuItem(item.route)) && (
                    <li>
                      <Link to="/#" className="has-arrow">
                        <span>{props.t("Memberships")}</span>
                      </Link>
                      <ul className="sub-menu">
                        {membershipsMenuItems.map((item) => 
                          shouldShowMenuItem(item.route) && (
                            <li key={item.route}>
                              <Link to={item.route}>{props.t(item.label)}</Link>
                            </li>
                          )
                        )}
                      </ul>
                    </li>
                  )}
                </ul>
              </li>
            )}

            {/* 2. Sales & Payments Menu */}
            {(salesPosMenuItems.some(item => shouldShowMenuItem(item.route)) || 
              receiptsPaymentsMenuItems.some(item => shouldShowMenuItem(item.route)) ||
              pettyCashMenuItems.some(item => shouldShowMenuItem(item.route)) ||
              dayClosingMenuItems.some(item => shouldShowMenuItem(item.route))) && (
              <li>
                <Link to="/#" className="has-arrow">
                  <i className="bx bx-receipt"></i>
                  <span>{props.t("Sales & Payments")}</span>
                </Link>
                <ul className="sub-menu">
                  {/* Sales (POS) Submenu */}
                  {salesPosMenuItems.some(item => shouldShowMenuItem(item.route)) && (
                    <li>
                      <Link to="/#" className="has-arrow">
                        <span>{props.t("Sales (POS)")}</span>
                      </Link>
                      <ul className="sub-menu">
                        {salesPosMenuItems.map((item) => 
                          shouldShowMenuItem(item.route) && (
                            <li key={item.route}>
                              <Link to={item.route}>{props.t(item.label)}</Link>
                            </li>
                          )
                        )}
                      </ul>
                    </li>
                  )}
                  
                  {/* Receipts & Payments Submenu */}
                  {receiptsPaymentsMenuItems.some(item => shouldShowMenuItem(item.route)) && (
                    <li>
                      <Link to="/#" className="has-arrow">
                        <span>{props.t("Receipts & Payments")}</span>
                      </Link>
                      <ul className="sub-menu">
                        {receiptsPaymentsMenuItems.map((item) => 
                          shouldShowMenuItem(item.route) && (
                            <li key={item.route}>
                              <Link to={item.route}>{props.t(item.label)}</Link>
                            </li>
                          )
                        )}
                      </ul>
                    </li>
                  )}
                  
                  {/* Petty Cash Submenu */}
                  {pettyCashMenuItems.some(item => shouldShowMenuItem(item.route)) && (
                    <li>
                      <Link to="/#" className="has-arrow">
                        <span>{props.t("Petty Cash")}</span>
                      </Link>
                      <ul className="sub-menu">
                        {pettyCashMenuItems.map((item) => 
                          shouldShowMenuItem(item.route) && (
                            <li key={item.route}>
                              <Link to={item.route}>{props.t(item.label)}</Link>
                            </li>
                          )
                        )}
                      </ul>
                    </li>
                  )}
                  
                  {/* Day Closing Submenu */}
                  {dayClosingMenuItems.some(item => shouldShowMenuItem(item.route)) && (
                    <li>
                      <Link to="/#" className="has-arrow">
                        <span>{props.t("Day Closing")}</span>
                      </Link>
                      <ul className="sub-menu">
                        {dayClosingMenuItems.map((item) => 
                          shouldShowMenuItem(item.route) && (
                            <li key={item.route}>
                              <Link to={item.route}>{props.t(item.label)}</Link>
                            </li>
                          )
                        )}
                      </ul>
                    </li>
                  )}
                </ul>
              </li>
            )}

            {/* 3. Services & Packages Menu */}
            {(servicesMenuItems.some(item => shouldShowMenuItem(item.route)) || 
              packagesMenuItems.some(item => shouldShowMenuItem(item.route))) && (
              <li>
                <Link to="/#" className="has-arrow">
                  <i className="bx bx-spa"></i>
                  <span>{props.t("Services & Packages")}</span>
                </Link>
                <ul className="sub-menu">
                  {/* Services Submenu */}
                  {servicesMenuItems.some(item => shouldShowMenuItem(item.route)) && (
                    <li>
                      <Link to="/#" className="has-arrow">
                        <span>{props.t("Services")}</span>
                      </Link>
                      <ul className="sub-menu">
                        {servicesMenuItems.map((item) => 
                          shouldShowMenuItem(item.route) && (
                            <li key={item.route}>
                              <Link to={item.route}>{props.t(item.label)}</Link>
                            </li>
                          )
                        )}
                      </ul>
                    </li>
                  )}
                  
                  {/* Packages Submenu */}
                  {packagesMenuItems.some(item => shouldShowMenuItem(item.route)) && (
                    <li>
                      <Link to="/#" className="has-arrow">
                        <span>{props.t("Packages")}</span>
                      </Link>
                      <ul className="sub-menu">
                        {packagesMenuItems.map((item) => 
                          shouldShowMenuItem(item.route) && (
                            <li key={item.route}>
                              <Link to={item.route}>{props.t(item.label)}</Link>
                            </li>
                          )
                        )}
                      </ul>
                    </li>
                  )}
                </ul>
              </li>
            )}

            {/* 4. Inventory & Suppliers Menu */}
            {(productsAssetsMenuItems.some(item => shouldShowMenuItem(item.route)) || 
              inventoryMenuItems.some(item => shouldShowMenuItem(item.route)) ||
              suppliersMenuItems.some(item => shouldShowMenuItem(item.route)) ||
              purchaseMenuItems.some(item => shouldShowMenuItem(item.route))) && (
              <li>
                <Link to="/#" className="has-arrow">
                  <i className="bx bx-package"></i>
                  <span>{props.t("Inventory & Suppliers")}</span>
                </Link>
                <ul className="sub-menu">
                  {/* Products & Assets Submenu */}
                  {productsAssetsMenuItems.some(item => shouldShowMenuItem(item.route)) && (
                    <li>
                      <Link to="/#" className="has-arrow">
                        <span>{props.t("Products & Assets")}</span>
                      </Link>
                      <ul className="sub-menu">
                        {productsAssetsMenuItems.map((item) => 
                          shouldShowMenuItem(item.route) && (
                            <li key={item.route}>
                              <Link to={item.route}>{props.t(item.label)}</Link>
                            </li>
                          )
                        )}
                      </ul>
                    </li>
                  )}
                  
                  {/* Inventory Submenu */}
                  {inventoryMenuItems.some(item => shouldShowMenuItem(item.route)) && (
                    <li>
                      <Link to="/#" className="has-arrow">
                        <span>{props.t("Inventory")}</span>
                      </Link>
                      <ul className="sub-menu">
                        {inventoryMenuItems.map((item) => 
                          shouldShowMenuItem(item.route) && (
                            <li key={item.route}>
                              <Link to={item.route}>{props.t(item.label)}</Link>
                            </li>
                          )
                        )}
                      </ul>
                    </li>
                  )}
                  
                  {/* Suppliers Submenu */}
                  {suppliersMenuItems.some(item => shouldShowMenuItem(item.route)) && (
                    <li>
                      <Link to="/#" className="has-arrow">
                        <span>{props.t("Suppliers")}</span>
                      </Link>
                      <ul className="sub-menu">
                        {suppliersMenuItems.map((item) => 
                          shouldShowMenuItem(item.route) && (
                            <li key={item.route}>
                              <Link to={item.route}>{props.t(item.label)}</Link>
                            </li>
                          )
                        )}
                      </ul>
                    </li>
                  )}
                  
                  {/* Purchase Submenu */}
                  {purchaseMenuItems.some(item => shouldShowMenuItem(item.route)) && (
                    <li>
                      <Link to="/#" className="has-arrow">
                        <span>{props.t("Purchase")}</span>
                      </Link>
                      <ul className="sub-menu">
                        {purchaseMenuItems.map((item) => 
                          shouldShowMenuItem(item.route) && (
                            <li key={item.route}>
                              <Link to={item.route}>{props.t(item.label)}</Link>
                            </li>
                          )
                        )}
                      </ul>
                    </li>
                  )}
                </ul>
              </li>
            )}

            {/* 5. Human Resources Menu */}
            {(employeesMenuItems.some(item => shouldShowMenuItem(item.route)) || 
              attendanceLeaveMenuItems.some(item => shouldShowMenuItem(item.route)) || 
              salaryAdvanceRevisionMenuItems.some(item => shouldShowMenuItem(item.route)) ||
              payrollMenuItems.some(item => shouldShowMenuItem(item.route))) && (
              <li>
                <Link to="/#" className="has-arrow">
                  <i className="bx bx-user-circle"></i>
                  <span>{props.t("Human Resources")}</span>
                </Link>
                <ul className="sub-menu">
                  {/* Employees Submenu */}
                  {employeesMenuItems.some(item => shouldShowMenuItem(item.route)) && (
                    <li>
                      <Link to="/#" className="has-arrow">
                        <span>{props.t("Employees")}</span>
                      </Link>
                      <ul className="sub-menu">
                        {employeesMenuItems.map((item) => 
                          shouldShowMenuItem(item.route) && (
                            <li key={item.route}>
                              <Link to={item.route}>{props.t(item.label)}</Link>
                            </li>
                          )
                        )}
                      </ul>
                    </li>
                  )}
                  
                  {/* Attendance & Leave Submenu */}
                  {attendanceLeaveMenuItems.some(item => shouldShowMenuItem(item.route)) && (
                    <li>
                      <Link to="/#" className="has-arrow">
                        <span>{props.t("Attendance & Leave")}</span>
                      </Link>
                      <ul className="sub-menu">
                        {attendanceLeaveMenuItems.map((item) => 
                          shouldShowMenuItem(item.route) && (
                            <li key={item.route}>
                              <Link to={item.route}>{props.t(item.label)}</Link>
                            </li>
                          )
                        )}
                      </ul>
                    </li>
                  )}
                  
                  {/* Salary Advance and Revision Submenu */}
                  {salaryAdvanceRevisionMenuItems.some(item => shouldShowMenuItem(item.route)) && (
                    <li>
                      <Link to="/#" className="has-arrow">
                        <span>{props.t("Salary Advance and Revision")}</span>
                      </Link>
                      <ul className="sub-menu">
                        {salaryAdvanceRevisionMenuItems.map((item) => 
                          shouldShowMenuItem(item.route) && (
                            <li key={item.route}>
                              <Link to={item.route}>{props.t(item.label)}</Link>
                            </li>
                          )
                        )}
                      </ul>
                    </li>
                  )}
                  
                  {/* Payroll Submenu */}
                  {payrollMenuItems.some(item => shouldShowMenuItem(item.route)) && (
                    <li>
                      <Link to="/#" className="has-arrow">
                        <span>{props.t("Payroll")}</span>
                      </Link>
                      <ul className="sub-menu">
                        {payrollMenuItems.map((item) => 
                          shouldShowMenuItem(item.route) && (
                            <li key={item.route}>
                              <Link to={item.route}>{props.t(item.label)}</Link>
                            </li>
                          )
                        )}
                      </ul>
                    </li>
                  )}
                </ul>
              </li>
            )}

            {/* 6. Reports Menu */}
            {(salesOperationalReportsMenuItems.some(item => shouldShowMenuItem(item.route)) || 
              employeePerformanceReportsMenuItems.some(item => shouldShowMenuItem(item.route)) || 
              hrReportsMenuItems.some(item => shouldShowMenuItem(item.route))) && (
              <li>
                <Link to="/#" className="has-arrow">
                  <i className="bx bx-file"></i>
                  <span>{props.t("Reports")}</span>
                </Link>
                <ul className="sub-menu">
                  {/* Sales & Operational Reports Submenu */}
                  {salesOperationalReportsMenuItems.some(item => shouldShowMenuItem(item.route)) && (
                    <li>
                      <Link to="/#" className="has-arrow">
                        <span>{props.t("Sales & Operational Reports")}</span>
                      </Link>
                      <ul className="sub-menu">
                        {salesOperationalReportsMenuItems.map((item) => 
                          shouldShowMenuItem(item.route) && (
                            <li key={item.route}>
                              <Link to={item.route}>{props.t(item.label)}</Link>
                            </li>
                          )
                        )}
                      </ul>
                    </li>
                  )}
                  
                  {/* Employee Performance Reports Submenu */}
                  {employeePerformanceReportsMenuItems.some(item => shouldShowMenuItem(item.route)) && (
                    <li>
                      <Link to="/#" className="has-arrow">
                        <span>{props.t("Employee Performance Reports")}</span>
                      </Link>
                      <ul className="sub-menu">
                        {employeePerformanceReportsMenuItems.map((item) => 
                          shouldShowMenuItem(item.route) && (
                            <li key={item.route}>
                              <Link to={item.route}>{props.t(item.label)}</Link>
                            </li>
                          )
                        )}
                      </ul>
                    </li>
                  )}
                  
                  {/* HR Reports Submenu */}
                  {hrReportsMenuItems.some(item => shouldShowMenuItem(item.route)) && (
                    <li>
                      <Link to="/#" className="has-arrow">
                        <span>{props.t("HR Reports")}</span>
                      </Link>
                      <ul className="sub-menu">
                        {hrReportsMenuItems.map((item) => 
                          shouldShowMenuItem(item.route) && (
                            <li key={item.route}>
                              <Link to={item.route}>{props.t(item.label)}</Link>
                            </li>
                          )
                        )}
                      </ul>
                    </li>
                  )}
                </ul>
              </li>
            )}

            {/* 7. Settings Menu (Admin Only) */}
            {isUserAdmin() && (businessSetupMenuItems.some(item => shouldShowMenuItem(item.route)) || 
              financeSetupMenuItems.some(item => shouldShowMenuItem(item.route)) ||
              userManagementMenuItems.some(item => shouldShowMenuItem(item.route)) || 
              notificationsMenuItems.some(item => shouldShowMenuItem(item.route))) && (
              <li>
                <Link to="/#" className="has-arrow">
                  <i className="bx bx-cog"></i>
                  <span>{props.t("Settings")}</span>
                </Link>
                <ul className="sub-menu">
                  {/* Business Setup Submenu */}
                  {businessSetupMenuItems.some(item => shouldShowMenuItem(item.route)) && (
                    <li>
                      <Link to="/#" className="has-arrow">
                        <span>{props.t("Business Setup")}</span>
                      </Link>
                      <ul className="sub-menu">
                        {businessSetupMenuItems.map((item) => 
                          shouldShowMenuItem(item.route) && (
                            <li key={item.route}>
                              <Link to={item.route}>{props.t(item.label)}</Link>
                            </li>
                          )
                        )}
                      </ul>
                    </li>
                  )}
                  
                  {/* Finance Setup Submenu */}
                  {financeSetupMenuItems.some(item => shouldShowMenuItem(item.route)) && (
                    <li>
                      <Link to="/#" className="has-arrow">
                        <span>{props.t("Finance Setup")}</span>
                      </Link>
                      <ul className="sub-menu">
                        {financeSetupMenuItems.map((item) => 
                          shouldShowMenuItem(item.route) && (
                            <li key={item.route}>
                              <Link to={item.route}>{props.t(item.label)}</Link>
                            </li>
                          )
                        )}
                      </ul>
                    </li>
                  )}
                  
                  {/* User Management Submenu */}
                  {userManagementMenuItems.some(item => shouldShowMenuItem(item.route)) && (
                    <li>
                      <Link to="/#" className="has-arrow">
                        <span>{props.t("User Management")}</span>
                      </Link>
                      <ul className="sub-menu">
                        {userManagementMenuItems.map((item) => 
                          shouldShowMenuItem(item.route) && (
                            <li key={item.route}>
                              <Link to={item.route}>{props.t(item.label)}</Link>
                            </li>
                          )
                        )}
                      </ul>
                    </li>
                  )}
                  
                  {/* Notifications Submenu */}
                  {notificationsMenuItems.some(item => shouldShowMenuItem(item.route)) && (
                    <li>
                      <Link to="/#" className="has-arrow">
                        <span>{props.t("Notifications")}</span>
                      </Link>
                      <ul className="sub-menu">
                        {notificationsMenuItems.map((item) => 
                          shouldShowMenuItem(item.route) && (
                            <li key={item.route}>
                              <Link to={item.route}>{props.t(item.label)}</Link>
                            </li>
                          )
                        )}
                      </ul>
                    </li>
                  )}
                </ul>
              </li>
            )}

            {/* 8. Others Menu */}
            {othersMenuItems.some(item => shouldShowMenuItem(item.route)) && (
              <li>
                <Link to="/#" className="has-arrow">
                  <i className="bx bx-dots-horizontal-rounded"></i>
                  <span>{props.t("Others")}</span>
                </Link>
                <ul className="sub-menu">
                  {othersMenuItems.map((item) => 
                    shouldShowMenuItem(item.route) && (
                      <li key={item.route}>
                        <Link to={item.route}>{props.t(item.label)}</Link>
                      </li>
                    )
                  )}
                </ul>
              </li>
            )}


          </ul>
        </div>
      </SimpleBar>
    </React.Fragment>
  )
}

SidebarContent.propTypes = {
  location: PropTypes.object,
  t: PropTypes.any,
  sidebarType: PropTypes.string,
}

export default withRouter(withTranslation()(SidebarContent))
